import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaBell, FaSignOutAlt, FaTimes, FaUserShield } from 'react-icons/fa';
import { HiOutlineX } from 'react-icons/hi';
import { useFriendRequests } from '../../contexts/friendRequests';
import { useNotifications } from '../../contexts/notifications';

const Drawer = ({ isOpen, onClose, menuItems, onLogout, pendingRequestsCount, user }) => {
  const location = useLocation();
  const { pendingRequests } = useFriendRequests();
  const { notificacoes, marcarComoLida } = useNotifications();

  // Fechar o drawer quando a rota muda
  useEffect(() => {
    if (isOpen) {
      onClose();
    }
  }, [location.pathname]);

  return (
    <>
      {/* Overlay */}
      <div 
        className={`fixed inset-0 bg-black/70 backdrop-blur-sm z-50 transition-opacity duration-300 ease-out
          ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} 
        onClick={onClose} 
      />
      
      {/* Drawer */}
      <div className={`fixed top-0 left-0 h-full w-80 max-w-[85vw] bg-gray-900/95 backdrop-blur-xl
        border-r border-gray-700/50 transform transition-transform duration-300 ease-out z-50 flex flex-col
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700/50">
          <div className="flex items-center gap-3">
            <div>
              <h2 className="text-lg font-bold text-white">Menu</h2>
              <p className="text-xs text-gray-400">Navegação</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 rounded-xl text-gray-400 hover:text-white hover:bg-gray-700/50 
              transition-all duration-200 ease-out group"
            aria-label="Fechar menu"
          >
            <HiOutlineX className="h-6 w-6 transition-transform duration-200 group-hover:scale-110" />
          </button>
        </div>

        {/* Perfil do usuário */}
        {user && (
          <div className="p-6 border-b border-gray-700/30">
            <Link
              to="/perfil"
              onClick={onClose}
              className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-700/50 
                transition-all duration-200 group"
            >
              {user.avatar ? (
                <img 
                  src={user.avatar} 
                  alt={user.nome || 'Usuário'}
                  className="w-12 h-12 rounded-full border-2 border-gray-600 group-hover:border-blue-500 
                    transition-all duration-200"
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 
                  flex items-center justify-center text-white text-lg font-semibold
                  border-2 border-gray-600 group-hover:border-blue-500 transition-all duration-200">
                  {user.nome ? user.nome.charAt(0).toUpperCase() : 'U'}
                </div>
              )}
              <div className="flex-1">
                <h3 className="text-white font-medium group-hover:text-blue-300 transition-colors duration-200">
                  {user.nome || 'Usuário'}
                </h3>
                <p className="text-gray-400 text-sm">Ver perfil</p>
              </div>
            </Link>
          </div>
        )}

        {/* Seção de Notificações - Mobile Only */}
        {notificacoes.length > 0 && (
          <div className="p-4 border-b border-gray-700/30">
            <div className="flex items-center gap-2 px-2 py-2 mb-3">
              <FaBell className="text-blue-400 text-sm" />
              <span className="text-blue-300 text-sm font-semibold">Notificações</span>
              <span className="bg-blue-500 text-white text-xs font-bold rounded-full 
                w-5 h-5 flex items-center justify-center ml-auto">
                {notificacoes.length > 9 ? '9+' : notificacoes.length}
              </span>
            </div>
            
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {notificacoes.slice(0, 5).map((notificacao) => (
                <div key={notificacao.id} 
                  className="flex items-start gap-3 p-3 bg-gray-800/50 rounded-lg
                    border border-gray-700/50 relative group">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-white text-xs font-medium truncate">
                        {notificacao.grupoNome || 'Grupo'}
                      </p>
                      <button
                        onClick={() => marcarComoLida(notificacao.id)}
                        className="text-gray-400 hover:text-white p-1 rounded
                          transition-colors touch-manipulation"
                        aria-label="Marcar como lida"
                      >
                        <FaTimes className="w-3 h-3" />
                      </button>
                    </div>
                    
                    <p className="text-gray-300 text-xs leading-relaxed">
                      <span className="font-medium">{notificacao.usuarioNome}</span>
                      {' '}
                      <span className={
                        notificacao.status === 'DENTRO' ? 'text-green-400' :
                        notificacao.status === 'DUVIDA' ? 'text-yellow-400' :
                        notificacao.status === 'FORA' ? 'text-red-400' : 'text-gray-400'
                      }>
                        {notificacao.status === 'DENTRO' ? 'confirmou presença' :
                         notificacao.status === 'DUVIDA' ? 'está em dúvida' :
                         notificacao.status === 'FORA' ? 'não vai comparecer' : notificacao.status}
                      </span>
                    </p>
                  </div>
                </div>
              ))}
              
              {notificacoes.length > 5 && (
                <div className="px-3 py-2 text-xs text-gray-400 text-center bg-gray-800/30 rounded-lg">
                  +{notificacoes.length - 5} outras notificações
                </div>
              )}
            </div>
          </div>
        )}

        {/* Navegação */}
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {menuItems.filter(item => !item.isAdminRoute).map((item) => (
            item.onClick ? (
              <button
                key={item.to}
                onClick={() => {
                  item.onClick();
                  onClose();
                }}
                className="w-full flex items-center gap-3 px-4 py-3 text-gray-300 hover:text-white 
                  hover:bg-gray-700/50 rounded-xl transition-all duration-200 group"
              >
                <span className="flex-shrink-0 text-lg transition-transform duration-200 group-hover:scale-110">
                  {item.icon}
                </span>
                <span className="font-medium">{item.label}</span>
              </button>
            ) : (
              <Link
                key={item.to}
                to={item.to}
                onClick={onClose}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group
                  ${location.pathname === item.to 
                    ? 'text-white bg-gradient-to-r from-blue-600/50 to-purple-600/50 border border-blue-500/30' 
                    : 'text-gray-300 hover:text-white hover:bg-gray-700/50'
                  }`}
              >
                <span className={`flex-shrink-0 text-lg transition-transform duration-200
                  ${location.pathname === item.to ? 'scale-110' : 'group-hover:scale-110'}`}>
                  {item.icon}
                </span>
                <span className="font-medium flex-1">{item.label}</span>
                {location.pathname === item.to && (
                  <div className="w-2 h-2 bg-white rounded-full" />
                )}
              </Link>
            )
          ))}
          
          {/* Seção Admin */}
          {menuItems.some(item => item.isAdminRoute) && (
            <>
              <div className="border-t border-gray-700/50 my-4 pt-4">
                <div className="px-4 py-2 flex items-center gap-2">
                  <FaUserShield className="text-yellow-500 text-sm" />
                  <span className="text-yellow-400 text-sm font-semibold">Administração</span>
                </div>
                {menuItems.filter(item => item.isAdminRoute).map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={onClose}
                    className={`flex items-center gap-3 px-4 py-3 ml-4 rounded-xl transition-all duration-200 group
                      ${location.pathname === item.to 
                        ? 'text-yellow-300 bg-yellow-500/20 border border-yellow-500/30' 
                        : 'text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/10'
                      }`}
                  >
                    <span className={`flex-shrink-0 transition-transform duration-200
                      ${location.pathname === item.to ? 'scale-110' : 'group-hover:scale-110'}`}>
                      {item.icon}
                    </span>
                    <span className="font-medium">{item.label}</span>
                  </Link>
                ))}
              </div>
            </>
          )}
          
          {/* Notificações de solicitações de amizade */}
          <div className="border-t border-gray-700/50 my-4 pt-4">
            <Link
              to="/amigos"
              onClick={onClose}
              className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:text-white 
                hover:bg-gray-700/50 rounded-xl transition-all duration-200 group relative"
            >
              <span className="flex-shrink-0 text-lg transition-transform duration-200 group-hover:scale-110">
                <FaBell />
              </span>
              <span className="font-medium flex-1">Solicitações</span>
              {pendingRequestsCount > 0 && (
                <span className="bg-red-500 text-white text-xs font-bold rounded-full 
                  w-6 h-6 flex items-center justify-center">
                  {pendingRequestsCount > 9 ? '9+' : pendingRequestsCount}
                </span>
              )}
            </Link>

            {/* Preview das solicitações pendentes */}
            {pendingRequests.length > 0 && (
              <div className="ml-4 mt-2 space-y-2">
                {pendingRequests.slice(0, 3).map(request => (
                  <div key={request.id} 
                    className="flex items-center gap-3 px-3 py-2 bg-gray-800/50 rounded-lg">
                    <img 
                      src={request.remetente.avatar} 
                      alt={request.remetente.nome}
                      className="w-8 h-8 rounded-full border border-gray-600"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-300 truncate font-medium">
                        {request.remetente.nome}
                      </p>
                      <p className="text-xs text-gray-500">Solicitação de amizade</p>
                    </div>
                  </div>
                ))}
                {pendingRequests.length > 3 && (
                  <div className="px-3 py-2 text-xs text-gray-400 text-center bg-gray-800/30 rounded-lg">
                    +{pendingRequests.length - 3} outras solicitações
                  </div>
                )}
              </div>
            )}
          </div>
        </nav>

        {/* Footer com botão de logout */}
        <div className="p-4 border-t border-gray-700/50">
          <button
            onClick={() => {
              onLogout();
              onClose();
            }}
            className="w-full flex items-center gap-3 px-4 py-3 text-red-400 hover:text-red-300 
              hover:bg-red-500/10 rounded-xl transition-all duration-200 group"
          >
            <FaSignOutAlt className="flex-shrink-0 transition-transform duration-200 group-hover:scale-110" />
            <span className="font-medium">Sair da conta</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default Drawer;
